import React, { useEffect, useState } from 'react';
import { FiActivity, FiCheckCircle, FiLock, FiShuffle, FiUnlock, FiX, FiZap } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { CoinsIcon } from '../assets/icons/CoinsIcon';
import Flag from 'react-world-flags';
import Avatar from '../components/Avatar';
import ExpertPickPanel from '../components/ExpertPickPanel';
import StoryViewer from '../components/story/StoryViewer';
import { ROUTES } from '../constants';
import { avatarConfigDisplayColors } from '../constants/avatarPalette';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { apiGet, apiPost } from '../utils/api';
import {
  BetRecommendation,
  PersonalSummary,
  tipsterApi,
} from '../services/tipsterService';
import puckDropImage from '../assets/images/puck_drop.jpg';
import '../styles/expert-panel.scss';
import { TipsterPublic } from '../types/interfaces';

const TIER_COLORS = ['#58C9FA', '#3fb950', '#E07B39'];

const CTA_DISMISS_KEY = 'ep_cta_dismissed_at';
const CTA_DISMISS_HOURS = 6;

function isCTADismissed(): boolean {
  const raw = localStorage.getItem(CTA_DISMISS_KEY);
  if (!raw) return false;
  return Date.now() - Number(raw) < CTA_DISMISS_HOURS * 60 * 60 * 1000;
}

function formatLastPick(days: number | null): string {  if (days === null) return '—';
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

const ExpertPanel: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [tipster, setTipster] = useState<TipsterPublic | null>(null);
  const [summary, setSummary] = useState<PersonalSummary | null>(null);
  const [picks, setPicks] = useState<BetRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [storyOpen, setStoryOpen] = useState(false);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [openingDashboard, setOpeningDashboard] = useState(false);
  const [ctaDismissed, setCtaDismissed] = useState(isCTADismissed);

  const dismissCTA = () => {
    localStorage.setItem(CTA_DISMISS_KEY, String(Date.now()));
    setCtaDismissed(true);
  };

  const handleStripeDashboard = async () => {
    if (openingDashboard) return;
    setOpeningDashboard(true);
    try {
      const data = await apiGet<{ url: string }>('/tipsters/connect/dashboard-link');
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } finally {
      setOpeningDashboard(false);
    }
  };

  const handleStripeConnect = async () => {
    setConnectingStripe(true);
    try {
      const data = await apiPost<{ onboarding_url: string }>('/tipsters/connect/onboard');
      window.location.href = data.onboarding_url;
    } catch {
      setConnectingStripe(false);
    }
  };

  useEffect(() => {
    if (!user?.expert_id) return;
    Promise.all([
      tipsterApi.getById(user.expert_id),
      tipsterApi.getPersonalSummary(),
      tipsterApi.getMyRecommendationsActive(),
    ])
      .then(([tipsterData, summaryData, picksData]) => {
        setTipster(tipsterData);
        setSummary(summaryData);
        setPicks(picksData);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user?.expert_id]);

  if (loading) {
    return <div className="ep-dashboard ep-dashboard--loading">{t.common.loading}</div>;
  }

  const mon = summary?.monetization;
  const totalSubscribers = mon ? mon.tiers.reduce((s, t) => s + t.subscriber_count, 0) : 0;
  const sortedPicks = [...picks].sort((a, b) => {
    const da = a.bet_event?.game?.datetime ?? '';
    const db = b.bet_event?.game?.datetime ?? '';
    return db.localeCompare(da);
  });

  return (
    <>
      <section className="ep-dashboard">
        <div className="ep-dashboard__left">
          <div className="ep-dashboard__profile-card">
            {tipster?.avatar_matrix && tipster?.avatar_config && (
              <Avatar
                matrix={tipster.avatar_matrix}
                {...avatarConfigDisplayColors(tipster.avatar_config)}
                compact
              />
            )}
            <div className="ep-dashboard__profile-info">
              <div className="ep-dashboard__name-row">
                <h1 className="ep-dashboard__name">
                  {tipster?.full_name || `Expert #${user?.expert_id}`}
                </h1>
                {tipster?.is_verified && (
                  <FiCheckCircle size={15} className="ep-dashboard__verified" />
                )}
              </div>
              {tipster?.country && (
                <span className="ep-dashboard__country-pill">
                  <Flag code={tipster.country} className="ep-dashboard__flag" />
                  <span>{tipster.country}</span>
                </span>
              )}
            </div>
          </div>

          <div className="ep-dashboard__sidebar-buttons">
            <button
              className="ep-dashboard__action-btn"
              onClick={() => navigate('/expert-panel/recommendations/add')}
            >
              <FiZap size={14} />{t.manageRecommendations.addRecommendations}
            </button>
            <button
              className="ep-dashboard__action-btn"
              onClick={() => navigate(ROUTES.EXPERT_EDIT_PROFILE)}
            >
              <FiActivity size={14} />{t.expertPanel.editProfile}
            </button>
            {user?.expert_id && (
              <button
                className="ep-dashboard__action-btn"
                onClick={() => setStoryOpen(true)}
              >
                <FiShuffle size={14} />{t.expertPanel.checkYourStory}
              </button>
            )}
            {!summary?.monetization_active && (
              <button
                className="ep-dashboard__action-btn ep-dashboard__action-btn--stripe"
                onClick={handleStripeConnect}
                disabled={connectingStripe}
              >
                <FiZap size={14} />{connectingStripe ? 'Connecting…' : 'Connect Stripe'}
              </button>
            )}
            {summary?.monetization_active && (
              <button
                className="ep-dashboard__action-btn"
                onClick={() => navigate(ROUTES.EXPERT_MONETIZATION)}
              >
                <CoinsIcon width={14} height={14} />Manage monetization
              </button>
            )}
          </div>

          <div className="ep-dashboard__quick-stats card">
            <div className="ep-dashboard__quick-stat">
              <span className="ep-dashboard__quick-stat-value">
                {summary?.followers ?? 0}
              </span>
              <span className="ep-dashboard__quick-stat-label">Followers</span>
            </div>
            <div className="ep-dashboard__quick-stat">
              <span
                className="ep-dashboard__quick-stat-value"
                style={{
                  color:
                    summary?.roi_last_30_days != null && summary.roi_last_30_days < 0
                      ? 'var(--color-danger)'
                      : 'var(--color-success)',
                }}
              >
                {summary?.roi_last_30_days != null
                  ? `${summary.roi_last_30_days >= 0 ? '+' : ''}${summary.roi_last_30_days.toFixed(1)}%`
                  : '—'}
              </span>
              <span className="ep-dashboard__quick-stat-label">30d ROI</span>
            </div>
            <div className="ep-dashboard__quick-stat">
              <span className="ep-dashboard__quick-stat-value">
                {formatLastPick(summary?.days_since_last_pick ?? null)}
              </span>
              <span className="ep-dashboard__quick-stat-label">Last pick</span>
            </div>
          </div>
        </div>

        <div className="ep-dashboard__right">
          {summary?.monetization_active ? null : !ctaDismissed ? (
            <div className="ep-cta emphasized-card">
              <div className="ep-cta__header-row">
                <div className="ep-cta__header-left">
                  <h2 className="ep-cta__title">Turn your picks into income.</h2>
                  <button
                    className="ep-cta__connect-btn"
                    onClick={handleStripeConnect}
                    disabled={connectingStripe}
                  >
                    {connectingStripe ? 'Connecting…' : 'Connect Stripe →'}
                  </button>
                </div>
                <div className="ep-cta__header-right">
                  <div className="ep-cta__badge">
                    <FiZap size={11} />
                    Unlock earnings
                  </div>
                  <button className="ep-cta__dismiss" onClick={dismissCTA} aria-label="Dismiss">
                    <FiX size={15} />
                  </button>
                </div>
              </div>
              <div className="ep-cta__content">
                <p className="ep-cta__desc">
                  Connect a Stripe account and start offering paid tiers. Followers buy
                  30-day access in one payment — funds land in your bank. Your free picks
                  stay free.{' '}
                  <button className="ep-cta__how-link" type="button" onClick={() => navigate(ROUTES.HOW_PAYOUTS_WORK)}>How payouts work?</button>
                </p>
              </div>
              <div className="ep-cta__features">
                <div className="ep-cta__feature">
                  <FiCheckCircle size={14} className="ep-cta__feature-icon" />
                  <span className="ep-cta__feature-title">Set your own price</span>
                  <span className="ep-cta__feature-sub">You decide tier cost</span>
                </div>
                <div className="ep-cta__feature">
                  <FiUnlock size={14} className="ep-cta__feature-icon" />
                  <span className="ep-cta__feature-title">Direct payouts</span>
                  <span className="ep-cta__feature-sub">Stripe to your bank</span>
                </div>
                <div className="ep-cta__feature">
                  <FiLock size={14} className="ep-cta__feature-icon" />
                  <span className="ep-cta__feature-title">30-day access window</span>
                  <span className="ep-cta__feature-sub">Single one-time charge</span>
                </div>
              </div>
            </div>
          ) : null}
          {summary?.monetization_active && (
            <div className="ep-mon">
              <div className="ep-mon__top">
                <div className="ep-mon__top-left">
                  <span className="ep-mon__header-label">Lifetime earnings</span>
                  <span className="ep-mon__lifetime">${Number(mon?.lifetime ?? 0).toFixed(2)}</span>
                </div>
                <button
                  className="ep-dashboard__stripe-pill ep-dashboard__stripe-pill--link"
                  onClick={handleStripeDashboard}
                  disabled={openingDashboard}
                >
                  {openingDashboard ? 'opening…' : 'connected stripe'}
                  <span className="ep-dashboard__stripe-dot" />
                </button>
              </div>

              <div className="ep-mon__stats-row">
                <div className="ep-mon__stat">
                  <span className="ep-mon__stat-label">This month</span>
                  <span className="ep-mon__stat-value">${Number(mon?.this_month ?? 0).toFixed(2)}</span>
                </div>
                <div className="ep-mon__stat-divider" />
                <div className="ep-mon__stat">
                  <span className="ep-mon__stat-label">Active subscribers</span>
                  <span className="ep-mon__stat-value">{totalSubscribers}</span>
                </div>
              </div>

              {mon && mon.tiers.length > 0 && (
                <div className="ep-mon__tiers">
                  <div className="ep-mon__tiers-header">
                    <span className="ep-mon__tiers-label">Tier mix</span>
                    <span className="ep-mon__tiers-desc">{totalSubscribers} paying subscribers</span>
                  </div>
                  <div className="ep-mon__bar">
                    {mon.tiers.map((tier, i) => {
                      const pct = totalSubscribers > 0
                        ? (tier.subscriber_count / totalSubscribers) * 100
                        : 0;
                      return (
                        <div
                          key={tier.tier_id}
                          className="ep-mon__bar-seg"
                          style={{ width: `${pct}%`, background: TIER_COLORS[i % TIER_COLORS.length] }}
                        />
                      );
                    })}
                  </div>
                  <div className="ep-mon__tier-cards">
                    {mon.tiers.map((tier, i) => (
                      <div key={tier.tier_id} className="ep-mon__tier-card">
                        <div className="ep-mon__tier-card-top">
                          <span
                            className="ep-mon__tier-dot"
                            style={{ background: TIER_COLORS[i % TIER_COLORS.length] }}
                          />
                          <span className="ep-mon__tier-name">{tier.tier_name ?? 'Tier'}</span>
                        </div>
                        <span className="ep-mon__tier-count">{tier.subscriber_count}</span>
                        <span className="ep-mon__tier-sub">subscribers</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="ep-dashboard__picks-section">
            <span className="ep-dashboard__picks-label">Current picks</span>
          <div className="ep-dashboard__picks-list">
            {picks.length === 0 && (
              <p className="ep-dashboard__picks-empty">{t.expertStats.noPicksFound}</p>
            )}
            {sortedPicks.map((pick) => {
              const ongoing = pick.bet_event?.game?.datetime
                ? new Date(pick.bet_event.game.datetime) <= new Date()
                : false;
              return (
                <ExpertPickPanel key={pick.id} recommendation={pick} ongoing={ongoing} />
              );
            })}
          </div>
          </div>
        </div>
      </section>

      <div className="ep-banner">
        <img src={puckDropImage} alt="" className="ep-banner__image" />
        <div className="ep-banner__overlay" />
      </div>

      {storyOpen && (
        <StoryViewer isOpen={storyOpen} onClose={() => setStoryOpen(false)} />
      )}
    </>
  );
};

export default ExpertPanel;
